import { getFollowUpProgress } from "api/followUpDiscussion";

/**
 * Maps backend FollowUpStep enum values to frontend `{ section, slug }`.
 * Used when navigating from the listing to the most recently completed step
 * for a follow-up lead.
 */
const FOLLOW_UP_STEP_TO_PATH: Record<
  string,
  { section: "discussion" | "filing" | "status"; slug: string }
> = {
  FU_S1_01_PRELIMINARY_DISCUSSION: {
    section: "discussion",
    slug: "preliminary-discussion-with-the-client",
  },
  FU_S1_02_APPLICATION_DETAILS_FROM_CLIENT: {
    section: "discussion",
    slug: "application-details-from-client",
  },
  FU_S1_03_INVOICE_OF_ADVANCE_AMOUNT_TO_CLIENT: {
    section: "discussion",
    slug: "invoice-of-advance-amount-to-client",
  },
  FU_S1_04_ADVANCE_AMOUNT_FROM_CLIENT_FOR_FOLLOW_UP: {
    section: "discussion",
    slug: "advance-amount-from-client",
  },
  FU_S1_05_ANALYSIS_REPORT_ON_STATUS_OF_APPLICATION: {
    section: "discussion",
    slug: "analysis-report-on-the-status-of-application",
  },
  FU_S1_06_LEGAL_ADVICE_FOR_FOLLOW_UP: {
    section: "discussion",
    slug: "legal-advice",
  },
  FU_S1_07_INVOICE_TO_CLIENT_FOR_FOLLOW_UP: {
    section: "discussion",
    slug: "invoice-to-client-for-follow-up",
  },
  FU_S1_08_PAYMENT_FROM_CLIENT_FOR_FOLLOW_UP: {
    section: "discussion",
    slug: "payment-from-client",
  },
};

const DEFAULT_STEP = {
  section: "discussion" as const,
  slug: "preliminary-discussion-with-the-client",
};

/**
 * Resolves the in-app path for a follow-up lead's most recently completed
 * workflow step so the user lands directly on it after clicking a row.
 *
 * Falls back to the preliminary discussion step when the lead has no
 * completed steps yet, when the completed step has no implemented UI route,
 * or when the progress endpoint fails.
 */
export async function getFollowUpPathForLead(leadId: string): Promise<string> {
  try {
    const progress = await getFollowUpProgress(leadId);
    const lastCompleted = [...progress.steps]
      .reverse()
      .find((s) => s.isCompleted)?.slug;
    const stepKey = lastCompleted ?? progress.maxStepReached ?? null;
    const pathSpec = stepKey ? FOLLOW_UP_STEP_TO_PATH[stepKey] : null;
    const { section, slug } = pathSpec ?? DEFAULT_STEP;
    return `/dashboard/follow-ups/new/${section}/${slug}/${leadId}`;
  } catch {
    return `/dashboard/follow-ups/new/discussion/preliminary-discussion-with-the-client/${leadId}`;
  }
}
